import { ErrorBoundaryRetry } from "@package/ui-components";
import { useEffect } from "react";
import { type FlyoutProps } from "../../utils/flyout-props";
import { useTriviaContext } from "./context-trivia";
import { TriviaContextProvider } from "./context-trivia-provider";
import { TriviaContainer } from "./TriviaContainer/TriviaContainer";
import { TriviaFeedback } from "./TriviaFeedback/TriviaFeedback";
import { TriviaQuestion } from "./TriviaQuestion/TriviaQuestion";
import { TriviaResult } from "./TriviaResult/TriviaResult";
import { TriviaWelcome } from "./TriviaWelcome/TriviaWelcome";

// The wall's own flyout chrome (FlyoutCloseButton, gated on post.flyoutShowCloseButton) already
// floats a close control over every flyout regardless of its internal state, so this state has
// no close affordance of its own — Trivia never needs to duplicate it.
function TriviaEmptyState() {
  return (
    <div className="flex h-full min-h-0 w-full flex-col items-center justify-center gap-[var(--trivia-space)] px-[var(--trivia-space)] text-center">
      <div
        className="text-balance break-words font-bold"
        style={{ fontSize: "var(--trivia-text-prompt)" }}
      >
        This quiz isn&apos;t available right now.
      </div>
    </div>
  );
}

function FlyoutTriviaContent() {
  const { config, issues, machine, currentQuestion } = useTriviaContext();

  useEffect(
    function effectOnTriviaIssuesChanged() {
      for (const issue of issues) {
        const log = issue.level === "error" ? console.error : console.warn;
        log(`[Trivia] ${issue.path}: ${issue.message}`);
      }
    },
    [issues],
  );

  if (config.questions.length === 0) {
    return (
      <TriviaContainer>
        <TriviaEmptyState />
      </TriviaContainer>
    );
  }

  return (
    <TriviaContainer>
      {machine.phase === "welcome" && <TriviaWelcome />}
      {machine.phase === "question" && (
        <TriviaQuestion key={currentQuestion?.id} />
      )}
      {machine.phase === "feedback" && (
        <TriviaFeedback key={currentQuestion?.id} />
      )}
      {machine.phase === "result" && <TriviaResult />}
    </TriviaContainer>
  );
}

export function FlyoutTrivia({
  screen,
  disableAnalytics,
}: FlyoutProps & { disableAnalytics?: boolean }) {
  return (
    <ErrorBoundaryRetry attemptLimit={1}>
      <TriviaContextProvider
        screen={screen}
        disableAnalytics={disableAnalytics}
      >
        <FlyoutTriviaContent />
      </TriviaContextProvider>
    </ErrorBoundaryRetry>
  );
}
